// src/pages/settings/PriceList.jsx
import React, { useEffect, useState, Suspense } from 'react';
import { Box, Heading, Text, Spinner, VStack, HStack } from '@chakra-ui/react';
import { getAuth } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';

// Hooks
import useOrgClaims from '../../hooks/useOrgClaims';

// Keep the same lazy import approach as Settings.js
const PriceEditor = React.lazy(() => import('../../components/PriceEditor'));

export default function PriceList() {
  // Prefer claims, but fall back to /admins/<uid>.orgId if needed (same behaviour you had in Settings.js)
  const { orgId: claimOrgId, loading: claimsLoading } = useOrgClaims();
  const [orgId, setOrgId] = useState(null);
  const [orgSource, setOrgSource] = useState(null); // 'claims' | 'adminsDoc' | null
  const [ready, setReady] = useState(false);

  // NEW: platform-level super admin flag (global Fixihub owner)
  const [canImportMaster, setCanImportMaster] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const resolveOrgAndRole = async () => {
      const auth = getAuth();
      const user = auth.currentUser;

      // Always check global /admins/{uid} to know if this user is a platform super admin
      if (user) {
        try {
          const globalAdminSnap = await getDoc(doc(db, 'admins', user.uid));
          if (!cancelled) setCanImportMaster(globalAdminSnap.exists() === true);
        } catch {
          if (!cancelled) setCanImportMaster(false);
        }
      } else {
        if (!cancelled) setCanImportMaster(false);
      }

      // 1) If claims has orgId, prefer it
      if (claimOrgId) {
        if (!cancelled) {
          setOrgId(claimOrgId);
          setOrgSource('claims');
          setReady(true);
        }
        return;
      }

      // 2) If claims still loading, wait
      if (claimsLoading) return;

      // 3) Fallback to /admins/<uid>.orgId (legacy support)
      if (!user) {
        if (!cancelled) {
          setOrgId(null);
          setOrgSource(null);
          setReady(true);
        }
        return;
      }

      try {
        const adminSnap = await getDoc(doc(db, 'admins', user.uid));
        const adminOrg = adminSnap.exists() ? adminSnap.data()?.orgId : null;
        if (!cancelled) {
          if (typeof adminOrg === 'string' && adminOrg.length > 0) {
            setOrgId(adminOrg);
            setOrgSource('adminsDoc');
          } else {
            setOrgId(null);
            setOrgSource(null);
          }
          setReady(true);
        }
      } catch (e) {
        if (!cancelled) {
          console.warn('[PriceList] org resolution failed:', e);
          setOrgId(null);
          setOrgSource(null);
          setReady(true);
        }
      }
    };

    resolveOrgAndRole();
    return () => {
      cancelled = true;
    };
  }, [claimOrgId, claimsLoading]);

  // While we’re figuring out org / claims, show a spinner (mirrors Settings experience)
  if (!ready) {
    return (
      <HStack spacing={4}>
        <Spinner size="lg" />
        <Text fontSize="lg">Loading…</Text>
      </HStack>
    );
  }

  return (
    <Box borderWidth={1} borderRadius="lg" p={6} mt={0} boxShadow="lg" bg="white">
      <VStack align="stretch" spacing={6}>
        {!orgId ? (
          <VStack align="flex-start" spacing={1}>
            <Heading size="lg" color="blue.600">
              Price List
            </Heading>
            <Text fontSize="md" color="gray.600">
              An organisation is required to edit price lists.
            </Text>
          </VStack>
        ) : (
          <>
            <VStack align="flex-start" spacing={1}>
              <Heading size="lg" color="blue.600">
                Price List
              </Heading>
              <Text fontSize="md" color="gray.600">
                For Doors pricing use the "Manage Doors" under Settings.
              </Text>
            </VStack>
            <Suspense fallback={<Spinner size="lg" />}>
              {/* Pass orgId explicitly as requested, plus canImportMaster to gate the importer */}
              <PriceEditor orgId={orgId} orgSource={orgSource} canImportMaster={canImportMaster} />
            </Suspense>
          </>
        )}
      </VStack>
    </Box>
  );
}
